import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { I18nProvider } from "@/context/I18nContext";
import { Layout } from "@/components/Layout";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import AuthCallback from "@/pages/AuthCallback";

import Home from "@/pages/Home";
import AIScan from "@/pages/AIScan";
import SurfaceExplorer from "@/pages/SurfaceExplorer";
import SurfacePage from "@/pages/SurfacePage";
import Pricing from "@/pages/Pricing";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Solutions from "@/pages/Solutions";
import Services from "@/pages/Services";
import ForBusiness from "@/pages/ForBusiness";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Quote from "@/pages/Quote";
import Booking from "@/pages/Booking";
import Projects from "@/pages/Projects";
import Partners from "@/pages/Partners";
import SolutionPage from "@/pages/SolutionPage";
import ApplicationPage from "@/pages/ApplicationPage";
import ConsultationFlow from "@/pages/ConsultationFlow";
import Warranty from "@/pages/Warranty";
import Login from "@/pages/Login";
import SeoLanding from "@/pages/SeoLanding";
import CityLanding from "@/pages/CityLanding";
import MyErrah from "@/pages/dashboard/MyErrah";
import PartnerLogin from "@/pages/partner/PartnerLogin";
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import AdminDashboard from "@/pages/admin/AdminDashboard";

import ApplicatorNetwork from "@/pages/applicator/ApplicatorNetwork";
import ApplicatorApply from "@/pages/applicator/ApplicatorApply";
import ApplicatorLogin from "@/pages/applicator/ApplicatorLogin";
import ApplicatorLayout from "@/pages/applicator/ApplicatorLayout";
import ApplicatorDashboard from "@/pages/applicator/ApplicatorDashboard";
import ApplicatorServices from "@/pages/applicator/ApplicatorServices";
import ApplicatorNetworkTree from "@/pages/applicator/ApplicatorNetworkTree";
import ApplicatorStub from "@/pages/applicator/ApplicatorStub";
import ApplicatorJobs from "@/pages/applicator/ApplicatorJobs";
import ApplicatorEarnings from "@/pages/applicator/ApplicatorEarnings";
import ApplicatorPayouts from "@/pages/applicator/ApplicatorPayouts";
import JobConfirm from "@/pages/applicator/JobConfirm";
import ApplicatorTraining from "@/pages/applicator/ApplicatorTraining";
import TrainingCourse from "@/pages/applicator/TrainingCourse";
import ApplicatorCertification from "@/pages/applicator/ApplicatorCertification";
import ApplicatorProfile from "@/pages/applicator/ApplicatorProfile";
import VerifyCertificate from "@/pages/applicator/VerifyCertificate";
import PublicApplicatorProfile from "@/pages/applicator/PublicApplicatorProfile";
import AdminTraining from "@/pages/admin/AdminTraining";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminCommunications from "@/pages/admin/AdminCommunications";
import AdminApplicators from "@/pages/admin/AdminApplicators";
import AdminApplicatorServices from "@/pages/admin/AdminApplicatorServices";
import AdminApplicatorJobs from "@/pages/admin/AdminApplicatorJobs";
import AdminApplicatorPayouts from "@/pages/admin/AdminApplicatorPayouts";

const APP_ROLES = ["applicator", "master_applicator", "regional_partner"];
const ADMIN_ROLES = ["admin", "founder"];

const seoRoutes = [
  ["surface-protection", "Surface Protection", "Marble,Granite,Quartz,Tabletop,Countertop,Furniture"],
  ["marble-protection", "Marble Protection", "Marble"],
  ["granite-protection", "Granite Protection", "Granite"],
  ["tabletop-protection", "Tabletop Protection", "Tabletop"],
  ["kitchen-protection", "Kitchen Protection", "Kitchen Surface"],
  ["furniture-protection", "Furniture Protection", "Furniture"],
  ["glass-protection", "Glass Protection", "Glass"],
  ["bathroom-protection", "Bathroom Protection", "Bathroom Surface"],
  ["commercial-surface-protection", "Commercial Surface Protection", ""],
  ["hospitality-surface-protection", "Hospitality Surface Protection", ""],
];

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/ai-scan" element={<AIScan />} />
        <Route path="/protection" element={<SurfaceExplorer />} />
        <Route path="/protection/:slug" element={<SurfacePage />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:id" element={<ProductDetail />} />
        <Route path="/solutions" element={<Solutions />} />
        <Route path="/services" element={<Services />} />
        <Route path="/for-business" element={<ForBusiness />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/quote" element={<Quote />} />
        <Route path="/consultation" element={<ConsultationFlow />} />
        <Route path="/warranty" element={<Warranty />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/solutions/surface-protection-film" element={<SolutionPage variant="spf" />} />
        <Route path="/solutions/nano-coating" element={<SolutionPage variant="nano" />} />
        {["sofa-protection", "curtain-protection", "rug-carpet-protection", "dining-table-protection", "kitchen-countertop-protection"].map((s) => (
          <Route key={s} path={`/applications/${s}`} element={<ApplicationPage slug={s} />} />
        ))}
        <Route path="/booking" element={<ProtectedRoute><Booking /></ProtectedRoute>} />
        <Route path="/my-errah" element={<ProtectedRoute roles={["customer", "admin"]}><MyErrah /></ProtectedRoute>} />
        {seoRoutes.map(([slug, title, surfaces]) => (
          <Route key={slug} path={`/${slug}`} element={<SeoLanding slug={slug} title={title} surfaces={surfaces} />} />
        ))}
        {[["noida", "Noida"], ["delhi", "Delhi"], ["gurgaon", "Gurgaon"]].map(([slug, city]) => (
          <Route key={slug} path={`/${slug}`} element={<CityLanding slug={slug} city={city} />} />
        ))}
      </Route>
      <Route path="/login" element={<Login />} />
      <Route path="/partner/login" element={<PartnerLogin />} />
      <Route path="/partner/dashboard" element={<ProtectedRoute roles={["partner", "admin"]}><PartnerDashboard /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute roles={["admin"]}><AdminDashboard /></ProtectedRoute>} />
      <Route path="/admin/applicators" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminApplicators /></ProtectedRoute>} />
      <Route path="/admin/applicator-services" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminApplicatorServices /></ProtectedRoute>} />
      <Route path="/admin/applicator-jobs" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminApplicatorJobs /></ProtectedRoute>} />
      <Route path="/admin/applicator-payouts" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminApplicatorPayouts /></ProtectedRoute>} />
      <Route path="/admin/training" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminTraining tab="courses" /></ProtectedRoute>} />
      <Route path="/admin/training/courses" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminTraining tab="courses" /></ProtectedRoute>} />
      <Route path="/admin/training/assessments" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminTraining tab="assessments" /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/communications" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminCommunications /></ProtectedRoute>} />
      <Route path="/job/:id/confirm" element={<JobConfirm />} />
      <Route path="/verify-certificate/:id" element={<VerifyCertificate />} />
      <Route path="/applicator-profile/:code" element={<PublicApplicatorProfile />} />

      {/* Applicator Network — separate ecosystem */}
      <Route path="/applicator-network" element={<ApplicatorNetwork />} />
      <Route path="/applicator/apply" element={<ApplicatorApply />} />
      <Route path="/applicator/login" element={<ApplicatorLogin />} />
      <Route element={<ProtectedRoute roles={APP_ROLES} loginPath="/applicator/login"><ApplicatorLayout /></ProtectedRoute>}>
        <Route path="/applicator/dashboard" element={<ApplicatorDashboard />} />
        <Route path="/applicator/services" element={<ApplicatorServices />} />
        <Route path="/applicator/network" element={<ApplicatorNetworkTree />} />
        <Route path="/applicator/jobs" element={<ApplicatorJobs />} />
        <Route path="/applicator/earnings" element={<ApplicatorEarnings />} />
        <Route path="/applicator/payouts" element={<ApplicatorPayouts />} />
        <Route path="/applicator/referrals" element={<ApplicatorStub title="referrals" />} />
        <Route path="/applicator/training" element={<ApplicatorTraining />} />
        <Route path="/applicator/training/:cid" element={<TrainingCourse />} />
        <Route path="/applicator/certification" element={<ApplicatorCertification />} />
        <Route path="/applicator/support" element={<ApplicatorStub title="support" />} />
        <Route path="/applicator/profile" element={<ApplicatorProfile />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <I18nProvider>
        <BrowserRouter>
          <Toaster theme="dark" position="top-center" richColors />
          <AppRouter />
        </BrowserRouter>
      </I18nProvider>
    </AuthProvider>
  );
}
